import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import CostBreakdownRow, { fmt } from './CostBreakdownRow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATIC_FALLBACK = { INR: 1, EUR: 92.5, GBP: 108, USD: 86, SGD: 64, AED: 23.5 };

const blank = () => ({
  category: 'hotels', description: '', cost: 0, currency: 'INR', margin: 0,
  vendorId: '', revenueMethod: '', commissionPct: 0, fixedAmount: 0,
  markupType: 'percent', markupValue: 0, isProvisional: false,
  bookingRef: '', bookingStatus: 'confirmed',
});

// Mirrors backend `_cost_item_revenue` (routes/trips.py) so the live preview
// matches what will actually be saved. Always operates on costInr (already
// FX-converted) — never the raw foreign-currency cost.
function computeClientAddOn(it, costInr) {
  const method = (it.revenueMethod || '').trim().toLowerCase();
  if (!method || method === 'custom') {
    return Number(it.margin) || 0;
  }
  const commissionPct = Number(it.commissionPct) || 0;
  const fixedAmount = Number(it.fixedAmount) || 0;
  const markupValue = Number(it.markupValue) || 0;
  const isPercent = (it.markupType || 'percent') === 'percent';
  const markupAmt = isPercent ? (costInr * markupValue / 100) : markupValue;
  if (method === 'net_rate_markup') return markupAmt;
  if (method === 'commission') return 0;
  if (method === 'fixed_commission') return 0;
  if (method === 'commission_markup') return markupAmt;
  if (method === 'fixed_fee') return fixedAmount;
  return 0; // no_revenue
}

function computeRow(it, rates, markup) {
  const cur = (it.currency || 'INR').toUpperCase();
  const rawRate = cur === 'INR' ? 1 : (rates[cur] || STATIC_FALLBACK[cur] || 0);
  const effRate = cur === 'INR' ? 1 : (rawRate + (markup || 1));
  const cost = Number(it.cost) || 0;
  const costInr = Math.round(cost * effRate * 100) / 100;
  const clientAddOn = computeClientAddOn(it, costInr);
  const finalPrice = Math.round((costInr + clientAddOn) * 100) / 100;
  return Object.assign({}, it, {
    exchangeRate: Math.round(effRate * 10000) / 10000,
    costInr,
    margin: clientAddOn,
    finalPrice,
  });
}

export default function CostBreakdown({ value, onChange, onTotalChange }) {
  const items = Array.isArray(value) ? value : [];
  const [fx, setFx] = useState({
    rates: STATIC_FALLBACK, markupPerUnitInr: 1, source: 'static', fetchedAt: null,
  });
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(BACKEND_URL + '/api/fx/rates')
      .then((res) => { if (!cancelled) setFx(res.data || fx); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    axios.get(BACKEND_URL + '/api/accounting/vendors')
      .then((res) => { if (!cancelled) setVendors(Array.isArray(res.data) ? res.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => {
    setLoading(true);
    axios.get(BACKEND_URL + '/api/fx/rates')
      .then((res) => setFx(res.data || fx))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const computed = items.map((it) => computeRow(it, fx.rates, fx.markupPerUnitInr));
  const total = Math.round(computed.reduce((s, x) => s + (x.finalPrice || 0), 0) * 100) / 100;

  useEffect(() => {
    if (onTotalChange) onTotalChange(total);
    // eslint-disable-next-line
  }, [total]);

  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? Object.assign({}, it, patch) : it));
    onChange(next);
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange(items.concat([blank()]));

  return (
    <div className="space-y-4" data-testid="cost-breakdown">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base font-semibold text-slate-800">Item-wise Cost Breakdown</Label>
          <p className="text-xs text-slate-500 mt-0.5">
            {'FX '}<span className="font-medium">{fx.source === 'live' ? 'live' : 'static fallback'}</span>
            {fx.fetchedAt ? ' \u2022 updated ' + new Date(fx.fetchedAt).toLocaleTimeString() : ''}
            {' \u2022 markup \u20B9' + (fx.markupPerUnitInr || 1) + ' per unit'}
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="refresh-fx-btn">
          <RefreshCw className={'h-3.5 w-3.5 mr-1.5 ' + (loading ? 'animate-spin' : '')} />
          Refresh rates
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="cost-items-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Category</th>
              <th className="text-left px-3 py-2 font-semibold">Description / Supplier Ref</th>
              <th className="text-left px-3 py-2 font-semibold w-40">Vendor</th>
              <th className="text-right px-3 py-2 font-semibold w-28">Cost</th>
              <th className="text-left px-3 py-2 font-semibold w-24">Currency</th>
              <th className="text-right px-3 py-2 font-semibold w-32">{'Cost (\u20B9)'}</th>
              <th className="text-left px-3 py-2 font-semibold w-44">Revenue Method</th>
              <th className="text-right px-3 py-2 font-semibold w-32">{'Margin/Fee (\u20B9)'}</th>
              <th className="text-left px-3 py-2 font-semibold w-40">Booking</th>
              <th className="text-right px-3 py-2 font-semibold w-32">{'Final (\u20B9)'}</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {computed.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center text-slate-400 italic py-6 text-xs">
                  No cost items yet. Click "Add Item" to create the first line.
                </td>
              </tr>
            ) : computed.map((it, idx) => (
              <CostBreakdownRow
                key={idx}
                item={it}
                idx={idx}
                vendors={vendors}
                onUpdate={update}
                onRemove={() => remove(idx)}
              />
            ))}
          </tbody>
          {computed.length > 0 ? (
            <tfoot>
              <tr className="bg-teal-50 border-t-2 border-teal-200">
                <td colSpan={9} className="px-3 py-2 text-right font-semibold text-slate-700">Total cost (Base)</td>
                <td className="px-3 py-2 text-right font-bold text-teal-700 text-base" data-testid="cost-total">{fmt(total)}</td>
                <td></td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-cost-item-btn">
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add Item
      </Button>
    </div>
  );
}
